/**
 * Utility functions for Video Reviews page state management
 * Helper functions for managing drawer, modal, and editing states
 */

import { VideoReview } from "../../../types";
import { VideoReviewManagementState } from "../types/video-reviews-types";

/**
 * Reset video review management state to initial values
 * @returns Initial state object
 */
export const resetVideoReviewState = (): VideoReviewManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  editingReview: null,
  deletingReview: null,
});

/**
 * Open edit drawer with selected video review
 * @param setState - State setter function
 * @param review - Video review to edit
 */
export const openEditDrawer = (
  setState: React.Dispatch<React.SetStateAction<VideoReviewManagementState>>,
  review: VideoReview
): void => {
  setState((prev) => ({
    ...prev,
    editingReview: review,
    isDrawerOpen: true,
  }));
};

/**
 * Open create drawer for new video review
 * @param setState - State setter function
 */
export const openCreateDrawer = (
  setState: React.Dispatch<React.SetStateAction<VideoReviewManagementState>>
): void => {
  setState((prev) => ({
    ...prev,
    editingReview: null,
    isDrawerOpen: true,
  }));
};

/**
 * Close drawer and reset editing state
 * @param setState - State setter function
 */
export const closeDrawer = (
  setState: React.Dispatch<React.SetStateAction<VideoReviewManagementState>>
): void => {
  setState((prev) => ({
    ...prev,
    isDrawerOpen: false,
    editingReview: null,
  }));
};

/**
 * Open delete modal with selected video review
 * @param setState - State setter function
 * @param review - Video review to delete
 */
export const openDeleteModal = (
  setState: React.Dispatch<React.SetStateAction<VideoReviewManagementState>>,
  review: VideoReview
): void => {
  setState((prev) => ({
    ...prev,
    deletingReview: review,
    isDeleteModalOpen: true,
  }));
};

/**
 * Close delete modal and reset deleting state
 * @param setState - State setter function
 */
export const closeDeleteModal = (
  setState: React.Dispatch<React.SetStateAction<VideoReviewManagementState>>
): void => {
  setState((prev) => ({
    ...prev,
    isDeleteModalOpen: false,
    deletingReview: null,
  }));
};

/**
 * Get drawer title based on editing state
 * @param editingReview - Currently editing video review
 * @returns Drawer title string
 */
export const getDrawerTitle = (editingReview: VideoReview | null): string => {
  return editingReview ? "Редактировать видео-отзыв" : "Создать видео-отзыв";
};

/**
 * Get display name for video review (fallback through multiple fields)
 * @param review - Video review object
 * @returns Display name string
 */
export const getVideoReviewDisplayName = (review: VideoReview): string => {
  return review.name || review.customerName || review.title || "Без названия";
};

/**
 * Get thumbnail URL for video review (fallback through multiple fields)
 * @param review - Video review object
 * @returns Thumbnail URL string or null
 */
export const getVideoReviewThumbnail = (review: VideoReview): string | null => {
  return review.image || review.thumbnail || review.thumbnailUrl || null;
};

/**
 * Get video URL for video review (fallback through multiple fields)
 * @param review - Video review object
 * @returns Video URL string or null
 */
export const getVideoReviewVideoUrl = (review: VideoReview): string | null => {
  return review.video_link || review.videoUrl || null;
};

/**
 * Get content/description for video review (fallback through multiple fields)
 * @param review - Video review object
 * @returns Content string or null
 */
export const getVideoReviewContent = (review: VideoReview): string | null => {
  return review.description || review.content || null;
};

/**
 * Get role for video review (fallback through multiple fields)
 * @param review - Video review object
 * @returns Role string or null
 */
export const getVideoReviewRole = (review: VideoReview): string | null => {
  return review.role || review.customerRole || null;
};

/**
 * Get date for video review (fallback through multiple fields)
 * @param review - Video review object
 * @returns Date string or null
 */
export const getVideoReviewDate = (review: VideoReview): string | null => {
  return review.date || review.reviewDate || review.createdAt || null;
};
